import { createPortal } from 'react-dom';
import { Button } from '@/shared/components/Button';

/**
 * The idle-warning countdown — slice-3560 (ADR-0046). PH10's twin of PB's
 * `w_phfc_timer_countdown` (`phfc_system_windows/w_phfc_timer_countdown.srw`).
 *
 * <table>
 *   <tr><th>PB</th><th>PH10</th></tr>
 *   <tr><td>title `'WS Shutdown Time Countdown'`</td><td>"Session about to expire"</td></tr>
 *   <tr><td>`'Seconds to Shutdown: n / 60'` (`:72`)</td><td>live `M:SS`</td></tr>
 *   <tr><td>`&Resume` — the button's own declared caption is `&OK` (`:135`), overridden at
 *       `f_phfc_handle_sys_idle.srf:19`</td><td><b>Keep working</b></td></tr>
 *   <tr><td>`beep(1)` every second for 60 seconds (`:70`)</td><td>one chime on open, one at
 *       T-10s — see `chime.ts`</td></tr>
 *   <tr><td>`idle(0)` on open so it cannot recur (`:51`)</td><td>the hook stops renewing while
 *       this is open; only "Keep working" extends</td></tr>
 * </table>
 *
 * <p>Deliberately NOT built on `shared/components/Modal`: that one ships a close ✕ and a
 * backdrop-click dismiss, and a pure-dismiss affordance here would read as "I've handled it"
 * while the countdown kept running underneath. PB's countdown window has exactly one button
 * too. Escape is not wired for the same reason.
 */
export function SessionExpiryModal({
  open,
  secondsLeft,
  idleMinutes,
  renewing,
  onKeepWorking,
  onSignOut,
}: {
  open: boolean;
  secondsLeft: number;
  idleMinutes: number;
  renewing: boolean;
  onKeepWorking: () => void;
  onSignOut: () => void;
}) {
  if (!open) return null;

  const mm = Math.floor(Math.max(0, secondsLeft) / 60);
  const ss = Math.max(0, secondsLeft) % 60;
  const clock = `${mm}:${String(ss).padStart(2, '0')}`;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" aria-hidden />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="ph10-session-expiry-title"
        aria-describedby="ph10-session-expiry-body"
        data-modal="true"
        data-testid="session-expiry-modal"
        className="relative w-full max-w-md rounded-lg bg-white shadow-xl"
      >
        <header className="border-b px-5 py-3">
          <h2 id="ph10-session-expiry-title" className="text-base font-semibold text-slate-900">
            Session about to expire
          </h2>
        </header>
        <div id="ph10-session-expiry-body" className="space-y-3 px-5 py-4 text-sm text-slate-700">
          <p>
            You&rsquo;ve been idle for {idleMinutes} minutes. You&rsquo;ll be signed out in{' '}
            <span
              className="font-mono text-base font-semibold text-slate-900"
              // The countdown is the point of this dialog: announce each change to screen
              // readers rather than leaving them with a static "about to expire".
              aria-live="assertive"
              aria-atomic="true"
              data-testid="session-expiry-countdown"
            >
              {clock}
            </span>
            .
          </p>
          <p className="text-slate-600">Choose Keep working to stay signed in.</p>
        </div>
        <footer className="flex items-center justify-end gap-2 border-t bg-slate-50 px-5 py-3">
          <Button variant="secondary" onClick={onSignOut}>
            Sign out now
          </Button>
          <Button onClick={onKeepWorking} disabled={renewing} autoFocus>
            {renewing ? 'Keeping you signed in…' : 'Keep working'}
          </Button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
